import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Menu, X, Globe, Mail, MapPin, Send } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const LOGO = "https://customer-assets.emergentagent.com/job_2044eed8-25ea-4764-9087-dbb138639082/artifacts/y2q66bx0_1001291181.jpg";

function NewsletterForm() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const r = await api.post("/newsletter", { email });
      toast.success(r.data.already ? t("footer.newsletterAlready") : t("footer.newsletterSuccess"));
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex gap-2" data-testid="newsletter-form">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("footer.newsletterPlaceholder")}
        className="flex-1 px-3 py-2 rounded-md text-sm text-slate-900 placeholder:text-slate-400 bg-white/95 outline-none focus:ring-2 focus:ring-white"
        data-testid="newsletter-input"
      />
      <button
        type="submit"
        disabled={busy}
        className="bg-white text-[#d81b7a] px-3 rounded-md font-semibold text-sm hover:bg-pink-50 disabled:opacity-60 flex items-center gap-1"
        data-testid="newsletter-submit"
      >
        <Send size={14} />
      </button>
    </form>
  );
}

function AnnouncementBar() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/announcements").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  if (items.length === 0) return null;
  const text = items.map((a) => `★  ${lang === "fr" ? a.message_fr : a.message_en}`).join("        ");
  return (
    <div className="cupe-gradient text-white text-sm py-2 overflow-hidden" data-testid="announcement-bar">
      <div className="flex whitespace-nowrap marquee">
        <span className="px-4">{text}</span>
        <span className="px-4">{text}</span>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { lang, toggle, t } = useLang();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about") },
    { to: "/rights", label: t("nav.rights") },
    { to: "/bargaining", label: t("nav.bargaining") },
    { to: "/news", label: t("nav.news") },
    { to: "/events", label: t("nav.events") },
    { to: "/executive", label: t("nav.executive") },
    { to: "/gallery", label: t("nav.gallery") },
    { to: "/member-corner", label: t("nav.memberCorner") },
    { to: "/community", label: t("nav.community") },
    { to: "/resources", label: t("nav.resources") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <AnnouncementBar />

      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-200" data-testid="main-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 lg:h-24 gap-4">
            <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="logo-link">
              <img src={LOGO} alt="CUPE Local 4957" className="h-14 sm:h-16 lg:h-20 w-auto" />
            </Link>

            <nav className="hidden xl:flex items-center gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-${l.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? "text-[#d81b7a]" : "text-slate-700 hover:text-[#d81b7a]"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={toggle}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-300 hover:border-[#d81b7a] hover:text-[#d81b7a] text-sm font-semibold text-slate-700 transition-colors"
                data-testid="lang-toggle-btn"
                aria-label="Toggle language"
              >
                <Globe size={16} />
                <span>{lang === "en" ? "FR" : "EN"}</span>
              </button>

              {user ? (
                <div className="hidden md:flex items-center gap-2">
                  <Link
                    to={user.role === "admin" ? "/admin" : "/portal"}
                    className="px-3 py-2 text-sm font-semibold text-[#d81b7a] hover:underline"
                    data-testid="nav-portal"
                  >
                    {user.role === "admin" ? t("nav.admin") : t("nav.memberPortal")}
                  </Link>
                  <button
                    onClick={() => { logout(); navigate("/"); }}
                    className="px-3 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900"
                    data-testid="logout-btn"
                  >
                    {t("nav.logout")}
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="hidden md:inline-flex cupe-gradient text-white px-4 py-2 rounded-md font-semibold text-sm hover:opacity-95 min-h-[40px] items-center"
                  data-testid="nav-login"
                >
                  {t("nav.memberPortal")}
                </Link>
              )}

              <button
                className="xl:hidden p-2 text-slate-700"
                onClick={() => setOpen((v) => !v)}
                data-testid="mobile-menu-toggle"
                aria-label="Menu"
              >
                {open ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {open && (
          <div className="xl:hidden border-t border-slate-200 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `px-3 py-3 text-base font-medium border-b border-slate-100 ${isActive ? "text-[#d81b7a]" : "text-slate-700"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              {user ? (
                <>
                  <Link
                    to={user.role === "admin" ? "/admin" : "/portal"}
                    onClick={() => setOpen(false)}
                    className="px-3 py-3 text-base font-semibold text-[#d81b7a]"
                  >
                    {user.role === "admin" ? t("nav.admin") : t("nav.memberPortal")}
                  </Link>
                  <button
                    onClick={() => { logout(); setOpen(false); navigate("/"); }}
                    className="px-3 py-3 text-base text-left text-slate-600"
                  >
                    {t("nav.logout")}
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="mt-3 cupe-gradient text-white px-4 py-3 rounded-md font-semibold text-center"
                >
                  {t("nav.memberPortal")}
                </Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="cupe-gradient text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO} alt="" className="h-14 bg-white rounded p-1" />
              <div className="text-[11px] uppercase tracking-widest text-white/80 font-semibold leading-tight">
                Oakpark Retirement<br/>Community Employees
              </div>
            </div>
            <p className="text-white/90 max-w-md text-lg">{t("footer.tagline")}</p>
            <p className="text-white/70 text-xs mt-4">{t("footer.sister")}</p>
          </div>
          <div>
            <h4 className="font-display font-semibold text-white mb-3">{t("footer.quickLinks")}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/rights" className="text-white/90 hover:text-white hover:underline">{t("nav.rights")}</Link></li>
              <li><Link to="/bargaining" className="text-white/90 hover:text-white hover:underline">{t("nav.bargaining")}</Link></li>
              <li><Link to="/gallery" className="text-white/90 hover:text-white hover:underline">{t("nav.gallery")}</Link></li>
              <li><Link to="/resources" className="text-white/90 hover:text-white hover:underline">{t("nav.resources")}</Link></li>
              <li><Link to="/contact" className="text-white/90 hover:text-white hover:underline">{t("nav.contact")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display font-semibold text-white mb-3">{t("footer.newsletter")}</h4>
            <p className="text-sm text-white/80 mb-3">{t("footer.newsletterSub")}</p>
            <NewsletterForm />
            <h4 className="font-display font-semibold text-white mt-6 mb-3">{t("footer.contact")}</h4>
            <ul className="space-y-2 text-sm text-white/90">
              <li className="flex items-start gap-2"><Mail size={16} className="mt-0.5 flex-shrink-0" /><a href="mailto:cupelocal4957oakpark@gmail.com" className="hover:text-white hover:underline break-all">cupelocal4957oakpark@gmail.com</a></li>
              <li className="flex items-start gap-2"><MapPin size={16} className="mt-0.5 flex-shrink-0" /><span>{t("contact.officeValue")}</span></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/25">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/80">
            <div>© {new Date().getFullYear()} {t("footer.copyright")}</div>
            <div className="font-semibold tracking-wide">Powered by MTBL GROUP</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
